import { useState } from 'react';
import { Download, TrendingUp, Users } from 'react-feather';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';

interface SegmentationResultsProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationResults;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const ValidationCard = styled.div`
  background: #f0f7ff;
  border: 1px solid #b3d9ff;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  gap: 24px;
  align-items: center;
`;

const ValidationMetric = styled.div`
  flex: 1;
  text-align: center;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: #1d4ed8;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1d94c7;
`;

const SegmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 16px;
`;

const SegmentCard = styled.div<{ $color: string }>`
  background: #ffffff;
  border: 2px solid ${(p) => p.$color}40;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${(p) => p.$color}20;
  }
`;

const SegmentHeader = styled.div<{ $color: string }>`
  background: ${(p) => p.$color}10;
  padding: 16px;
  border-bottom: 2px solid ${(p) => p.$color}40;
`;

const SegmentTitle = styled.div<{ $color: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${(p) => p.$color};
  margin-bottom: 8px;
`;

const SegmentSize = styled.div`
  font-size: 14px;
  color: #626262;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SegmentBody = styled.div`
  padding: 16px;
`;

const SegmentNarrative = styled.div`
  font-size: 14px;
  color: #323232;
  line-height: 1.6;
  margin-bottom: 12px;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ActionItem = styled.div`
  font-size: 13px;
  color: #626262;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #28aae1;
`;

const ExpandButton = styled.button`
  width: 100%;
  padding: 8px;
  border: none;
  background: #fafafa;
  color: #626262;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #f2f2f2;
    color: #323232;
  }
`;

const ExportButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #28aae1;
  color: #ffffff;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: #1d94c7;
    transform: translateY(-1px);
  }
`;

const SEGMENT_COLORS = ['#28aae1', '#4cb847', '#565add', '#f59e0b', '#e12e40', '#8b5cf6'];

export function SegmentationResults({ queryId, queryIdentifier, value }: SegmentationResultsProps) {

  const handleExport = () => {
    // Navigate to export phase - this will be handled by workflow advancement
    window.location.hash = `#export-${queryIdentifier}`;
  };
  const [expandedSegments, setExpandedSegments] = useState<Set<number>>(new Set());

  const toggleExpand = (segmentId: number): void => {
    setExpandedSegments((prev) => {
      const next = new Set(prev);
      if (next.has(segmentId)) { next.delete(segmentId); } else { next.add(segmentId); }
      return next;
    });
  };

  const profiles = value.profiles;

  if (!profiles) {
    return (
      <Wrapper>
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#adadad' }}>
          Generating segment profiles...
        </div>
      </Wrapper>
    );
  }

  const { segments, validation } = profiles;

  return (
    <Wrapper>
      {validation && (
        <ValidationCard>
          <ValidationMetric>
            <MetricLabel>Silhouette Score</MetricLabel>
            <MetricValue>{validation.silhouette_score.toFixed(3)}</MetricValue>
          </ValidationMetric>
          <ValidationMetric>
            <MetricLabel>Stability</MetricLabel>
            <MetricValue>{Math.round(validation.stability * 100)}%</MetricValue>
          </ValidationMetric>
          <ValidationMetric>
            <MetricLabel>Total Segments</MetricLabel>
            <MetricValue>{segments.length}</MetricValue>
          </ValidationMetric>
        </ValidationCard>
      )}

      <SegmentGrid>
        {segments.map((segment, idx) => {
          const color = SEGMENT_COLORS[idx % SEGMENT_COLORS.length];
          const isExpanded = expandedSegments.has(segment.id);

          return (
            <SegmentCard key={segment.id} $color={color}>
              <SegmentHeader $color={color}>
                <SegmentTitle $color={color}>{segment.name}</SegmentTitle>
                <SegmentSize>
                  <Users size={16} />
                  {segment.size.toLocaleString()} HCPs ({Math.round(segment.pct * 100)}%)
                </SegmentSize>
              </SegmentHeader>
              <SegmentBody>
                <SegmentNarrative>
                  {isExpanded ? segment.narrative : segment.narrative.slice(0, 200) + '...'}
                </SegmentNarrative>

                {segment.commercial_actions && segment.commercial_actions.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#323232', marginBottom: 8 }}>
                      <TrendingUp size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      Commercial Actions
                    </div>
                    <ActionList>
                      {segment.commercial_actions.slice(0, isExpanded ? undefined : 3).map((action, i) => (
                        <ActionItem key={i}>{action}</ActionItem>
                      ))}
                    </ActionList>
                  </>
                )}
              </SegmentBody>
              <ExpandButton onClick={() => toggleExpand(segment.id)}>
                {isExpanded ? 'Show Less' : 'Show More'}
              </ExpandButton>
            </SegmentCard>
          );
        })}
      </SegmentGrid>

      <ExportButton onClick={handleExport}>
        <Download size={18} />
        Export Results & Create Dashboards
      </ExportButton>
    </Wrapper>
  );
}
